import {
    type ConditionalElement,
    type DraggableFormElement,
    type FormAnswerSelectedOption,
    FormElementAction,
    type FormElement,
    type FormElementFile,
    type FormElementGridCellAnswer,
    type FormElementOption,
    FormElementTypeId,
} from '@packages/clients';
import { type Grid } from './worksheets';

/**
 * @deprecated Use the equivalent from `@packages/clients` directly. Re-exported for backward compatibility.
 * Retained until all consumers are migrated.
 */
export { FormElementAction };

/**
 * @deprecated Use the equivalent from `@packages/clients` directly. Re-exported for backward compatibility.
 * Retained until all consumers are migrated.
 */
export { FormElementTypeId };

/**
 * @deprecated Use the equivalent from `@packages/clients` directly. Re-exported for backward compatibility.
 * Retained until all consumers are migrated.
 */
export type { ConditionalElement };

/**
 * @deprecated Use the equivalent from `@packages/clients` directly. Re-exported for backward compatibility.
 * Retained until all consumers are migrated.
 */
export type { DraggableFormElement };

/**
 * @deprecated Use the equivalent from `@packages/clients` directly. Re-exported for backward compatibility.
 * Retained until all consumers are migrated.
 */
export type { FormAnswerSelectedOption };

/**
 * @deprecated Worksheet consumers: use {@link WorksheetFormElementEntity} from `@packages/domain`. QMR
 * consumers may continue using this type until the QMR domain is migrated. Will be removed once all consumers
 * are migrated. Re-exported from `@packages/clients`.
 */
export type { FormElement };

/**
 * @deprecated Worksheet consumers: use {@link WorksheetFormElementFileEntity} from `@packages/domain`. QMR
 * consumers may continue using this type until the QMR domain is migrated. Will be removed once all consumers
 * are migrated. Re-exported from `@packages/clients`.
 */
export type { FormElementFile };

/**
 * @deprecated Use the equivalent from `@packages/clients` directly. Re-exported for backward compatibility.
 * Retained until all consumers are migrated.
 */
export type { FormElementGridCellAnswer };

/**
 * @deprecated Worksheet consumers: use {@link WorksheetFormElementOptionEntity} from `@packages/domain`. QMR
 * consumers may continue using this type until the QMR domain is migrated. Will be removed once all consumers
 * are migrated. Re-exported from `@packages/clients`.
 */
export type { FormElementOption };

export interface DraggableFormComponent extends DraggableFormElement {}

export interface FromAnswerSelectedOption {
    optionId: string;
    answerText?: string;
}

export interface FormAnswer {
    elementId: string;
    options: FromAnswerSelectedOption[];
}

export const DEFAULT_DYNAMIC_GRID_PLACEHOLDER = 'Table Label';
export const DEFAULT_DYNAMIC_GRID: Grid[] = [
    {
        rowLabel: 'Row Label 1',
        rowOrder: 1,
        subRows: [],
        columns: [
            {
                worksheetElementGridId: '',
                columnLabel: 'Column Label 1',
                columnOrder: 1,
                defaultValue: '',
                isRequired: false,
                subColumns: [],
            },
            {
                worksheetElementGridId: '',
                columnLabel: 'Column Label 2',
                columnOrder: 2,
                defaultValue: '',
                isRequired: false,
                subColumns: [],
            },
            {
                worksheetElementGridId: '',
                columnLabel: 'Column Label 3',
                columnOrder: 3,
                defaultValue: '',
                isRequired: false,
                subColumns: [],
            },
        ],
    },
    {
        rowLabel: 'Row Label 2',
        rowOrder: 2,
        subRows: [],
        columns: [
            {
                worksheetElementGridId: '',
                columnLabel: 'Column Label 1',
                columnOrder: 1,
                defaultValue: '',
                isRequired: false,
                subColumns: [],
            },
            {
                worksheetElementGridId: '',
                columnLabel: 'Column Label 2',
                columnOrder: 2,
                defaultValue: '',
                isRequired: false,
                subColumns: [],
            },
            {
                worksheetElementGridId: '',
                columnLabel: 'Column Label 3',
                columnOrder: 3,
                defaultValue: '',
                isRequired: false,
                subColumns: [],
            },
        ],
    },
];

export const TIRE_WEAR_OPTION_LABELS = {
    EvenAndEqualWear: 'Even and equal wear',
    Center: 'Center',
    OuterEdge: 'Outer Edge',
    SingleEdge: 'Single Edge',
    Cupped: 'Cupped',
    Diagonal: 'Diagonal',
} as const;

export type TireWearOptionLabel = (typeof TIRE_WEAR_OPTION_LABELS)[keyof typeof TIRE_WEAR_OPTION_LABELS];

export const TIRE_WEAR_OPTIONS: TireWearOptionLabel[] = [
    TIRE_WEAR_OPTION_LABELS.EvenAndEqualWear,
    TIRE_WEAR_OPTION_LABELS.Center,
    TIRE_WEAR_OPTION_LABELS.OuterEdge,
    TIRE_WEAR_OPTION_LABELS.SingleEdge,
    TIRE_WEAR_OPTION_LABELS.Cupped,
    TIRE_WEAR_OPTION_LABELS.Diagonal,
];

export const draggableFormElements: Record<FormElementTypeId, DraggableFormElement> = {
    [FormElementTypeId.TextHeader]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.TextHeader,
        formElementLabel: '',
        selectedOptions: [],
        options: [],
        title: 'Text: Header',
        description: 'Header',
        iconName: 'title',
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        required: false,
        formElementHasRule: false,
    },
    [FormElementTypeId.TextParagraph]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.TextParagraph,
        formElementLabel: '',
        selectedOptions: [],
        options: [],
        title: 'Text: Paragraph',
        description: 'Paragraph',
        iconName: 'paragraph',
        required: false,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
    },
    [FormElementTypeId.InputText]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.InputText,
        formElementLabel: 'Input Label',
        selectedOptions: [],
        options: [
            {
                optionId: '',
                optionValue: '',
                optionLabel: '',
                optionPlaceholder: '',
            },
        ],
        title: 'Question: Short Text Input',
        description: 'Short Text Input',
        iconName: 'short-text',
        required: false,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
    },
    [FormElementTypeId.InputTextarea]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.InputTextarea,
        formElementLabel: 'Input Label',
        selectedOptions: [],
        options: [
            {
                optionId: '',
                optionValue: '',
                optionLabel: '',
                optionPlaceholder: '',
            },
        ],
        title: 'Question: Long Text Input',
        description: 'Long Text Input',
        iconName: 'long-text',
        required: false,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
    },
    [FormElementTypeId.Rating]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.Rating,
        formElementLabel: 'Rate',
        selectedOptions: [],
        options: [
            {
                optionId: '',
                optionValue: '1',
                optionLabel: 'Needs Improvement',
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '2',
                optionLabel: 'Acceptable',
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '3',
                optionLabel: 'Good',
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '4',
                optionLabel: 'Very Good',
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '5',
                optionLabel: 'Awesome',
                optionPlaceholder: '',
            },
        ],
        title: 'Question: Likert Scale',
        description: 'Likert Scale',
        iconName: 'likert',
        required: false,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
    },
    [FormElementTypeId.RatingWithText]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.RatingWithText,
        formElementLabel: 'Rate',
        selectedOptions: [],
        options: [
            {
                optionId: '',
                optionValue: '1',
                optionLabel: 'Needs Improvement',
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '2',
                optionLabel: 'Acceptable',
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '3',
                optionLabel: 'Good',
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '4',
                optionLabel: 'Very Good',
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '5',
                optionLabel: 'Awesome',
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '',
                optionLabel: 'Add Comment',
                optionPlaceholder: '',
            },
        ],
        title: 'Question: Likert Scale With Long Text Input',
        description: 'Likert Scale With Long Text Input',
        iconName: 'copy',
        required: false,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
    },
    [FormElementTypeId.Checkbox]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.Checkbox,
        formElementLabel: 'Select All That Apply',
        selectedOptions: [],
        options: [
            {
                optionId: '',
                optionValue: '1',
                optionLabel: 'Option 1',
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '2',
                optionLabel: 'Option 2',
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '3',
                optionLabel: 'Option 3',
                optionPlaceholder: '',
            },
        ],
        title: 'Question: Checkbox Group',
        description: 'Checkbox Group',
        iconName: 'checkbox-group',
        required: false,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
    },
    [FormElementTypeId.Radio]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.Radio,
        formElementLabel: 'Select One',
        selectedOptions: [],
        options: [
            {
                optionId: '',
                optionValue: '1',
                optionLabel: 'Option 1',
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '2',
                optionLabel: 'Option 2',
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '3',
                optionLabel: 'Option 3',
                optionPlaceholder: '',
            },
        ],
        title: 'Question: Radio Group',
        description: 'Radio Group',
        iconName: 'radio-group',
        required: false,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
    },
    [FormElementTypeId.Date]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.Date,
        formElementLabel: 'Date',
        selectedOptions: [],
        options: [
            {
                optionId: '',
                optionValue: '',
                optionLabel: '',
                optionPlaceholder: 'Your placeholderDate here',
            },
        ],
        title: 'Question: Date',
        description: 'Date',
        iconName: 'long-text',
        required: false,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
    },
    [FormElementTypeId.Time]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.Time,
        formElementLabel: 'Time',
        selectedOptions: [],
        options: [
            {
                optionId: '',
                optionValue: '',
                optionLabel: '',
                optionPlaceholder: 'Your placeholderDate here',
            },
        ],
        title: 'Question: Time',
        description: 'Time',
        iconName: 'long-text',
        required: false,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
    },
    [FormElementTypeId.File]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.File,
        formElementLabel: 'File or Image',
        selectedOptions: [],
        title: 'Question: File or Image',
        description: 'File or Image',
        iconName: 'long-text',
        required: false,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
        options: [],
    },
    [FormElementTypeId.Dropdown]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.Dropdown,
        formElementLabel: 'Select One',
        selectedOptions: [],
        options: [
            {
                optionId: '',
                optionValue: '1',
                optionLabel: 'Option 1',
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '2',
                optionLabel: 'Option 2',
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '3',
                optionLabel: 'Option 3',
                optionPlaceholder: '',
            },
        ],
        title: 'Question: Dropdown',
        description: 'Dropdown',
        iconName: 'select',
        required: false,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
    },
    [FormElementTypeId.DateTime]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.DateTime,
        formElementLabel: 'Date and Time',
        selectedOptions: [],
        options: [
            {
                optionId: '',
                optionValue: '',
                optionLabel: '',
                optionPlaceholder: 'Your placeholderDate here',
            },
        ],
        title: 'Question: Date and Time',
        description: 'Date and Time',
        iconName: 'long-text',
        required: false,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
    },
    [FormElementTypeId.TireWear]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.TireWear,
        formElementLabel:
            'Select the illustration that best matches the wear pattern observed on the tire of the vehicle.',
        selectedOptions: [],
        options: [
            {
                optionId: '',
                optionValue: '1',
                optionLabel: TIRE_WEAR_OPTION_LABELS.EvenAndEqualWear,
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '2',
                optionLabel: TIRE_WEAR_OPTION_LABELS.Center,
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '3',
                optionLabel: TIRE_WEAR_OPTION_LABELS.OuterEdge,
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '4',
                optionLabel: TIRE_WEAR_OPTION_LABELS.SingleEdge,
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '5',
                optionLabel: TIRE_WEAR_OPTION_LABELS.Cupped,
                optionPlaceholder: '',
            },
            {
                optionId: '',
                optionValue: '6',
                optionLabel: TIRE_WEAR_OPTION_LABELS.Diagonal,
                optionPlaceholder: '',
            },
        ],
        title: 'Question: Tire Wear',
        description: 'Tire Wear',
        iconName: 'radio-group',
        required: false,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
    },
    [FormElementTypeId.VehicleNoiseGrid]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.VehicleNoiseGrid,
        formElementLabel: 'Vehicle Noise Grid',
        selectedOptions: [],
        options: [],
        title: 'Question: Vehicle Noise Grid',
        description: 'Vehicle Noise Grid',
        iconName: 'image-grid',
        required: false,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
        grid: [
            {
                rowLabel: '1',
                rowOrder: 1,
                columns: [
                    {
                        columnLabel: 'General',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: 'A',
                                columnOrder: 1,
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: 'B',
                                columnOrder: 2,
                                isRequired: false,
                            },
                        ],
                    },
                    {
                        columnLabel: 'Bottom',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: 'C',
                                columnOrder: 3,
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: 'D',
                                columnOrder: 4,
                                isRequired: false,
                            },
                        ],
                    },
                ],
            },
            {
                rowLabel: '2',
                rowOrder: 2,
                columns: [
                    {
                        columnLabel: 'General',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: 'A',
                                columnOrder: 1,
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: 'B',
                                columnOrder: 2,
                                isRequired: false,
                            },
                        ],
                    },
                    {
                        columnLabel: 'Bottom',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: 'C',
                                columnOrder: 3,
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: 'D',
                                columnOrder: 4,
                                isRequired: false,
                            },
                        ],
                    },
                ],
            },
            {
                rowLabel: '3',
                rowOrder: 3,
                columns: [
                    {
                        columnLabel: 'General',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: 'A',
                                columnOrder: 1,
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: 'B',
                                columnOrder: 2,
                                isRequired: false,
                            },
                        ],
                    },
                    {
                        columnLabel: 'Bottom',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: 'C',
                                columnOrder: 3,
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: 'D',
                                columnOrder: 4,
                                isRequired: false,
                            },
                        ],
                    },
                ],
            },
        ],
    },
    [FormElementTypeId.DynamicGrid]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.DynamicGrid,
        formElementLabel: 'Question / Instruction',
        formElementPlaceholder: DEFAULT_DYNAMIC_GRID_PLACEHOLDER,
        selectedOptions: [],
        options: [],
        title: 'Table: Dynamic Grid',
        description: 'Dynamic Grid',
        iconName: 'short-text',
        required: true,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
        grid: DEFAULT_DYNAMIC_GRID,
    },
    [FormElementTypeId.PressureTestGrid]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.PressureTestGrid,
        formElementLabel: 'Question / Instruction',
        formElementPlaceholder: 'Lever Position',
        selectedOptions: [],
        options: [],
        title: 'Table: Line Pressure Test',
        description: 'Line Pressure Test',
        iconName: 'short-text',
        required: true,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
        grid: [
            {
                rowLabel: 'Drive',
                rowOrder: 1,
                columns: [],
                subRows: [
                    {
                        subRowLabel: 'Fully Closed',
                        rowOrder: 1,
                        columns: [
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Accel. opening angle (%)',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'ATF temp. (°F)',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Target pressure (PSI)',
                                columnOrder: 3,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Gauge pressure (PSI)',
                                columnOrder: 4,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Engine speed (RPM)',
                                columnOrder: 5,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                    {
                        subRowLabel: 'Fully Open',
                        rowOrder: 2,
                        columns: [
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Accel. opening angle (%)',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'ATF temp. (°F)',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Target pressure (PSI)',
                                columnOrder: 3,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Gauge pressure (PSI)',
                                columnOrder: 4,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Engine speed (RPM)',
                                columnOrder: 5,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                ],
            },
            {
                rowLabel: '2nd Gear Manual',
                rowOrder: 2,
                columns: [],
                subRows: [
                    {
                        subRowLabel: 'Fully Closed',
                        rowOrder: 1,
                        columns: [
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Accel. opening angle (%)',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'ATF temp. (°F)',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Target pressure (PSI)',
                                columnOrder: 3,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Gauge pressure (PSI)',
                                columnOrder: 4,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Engine speed (RPM)',
                                columnOrder: 5,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                    {
                        subRowLabel: 'Fully Open',
                        rowOrder: 2,
                        columns: [
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Accel. opening angle (%)',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'ATF temp. (°F)',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Target pressure (PSI)',
                                columnOrder: 3,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Gauge pressure (PSI)',
                                columnOrder: 4,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Engine speed (RPM)',
                                columnOrder: 5,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                ],
            },
            {
                rowLabel: 'Reverse',
                rowOrder: 3,
                columns: [],
                subRows: [
                    {
                        subRowLabel: 'Fully Open',
                        rowOrder: 1,
                        columns: [
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Accel. opening angle (%)',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'ATF temp. (°F)',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Target pressure (PSI)',
                                columnOrder: 3,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Gauge pressure (PSI)',
                                columnOrder: 4,
                                defaultValue: '',
                                isRequired: true,
                            },
                            {
                                worksheetElementGridId: '',
                                columnLabel: 'Engine speed (RPM)',
                                columnOrder: 5,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                ],
            },
        ],
    },
    [FormElementTypeId.ValveClearanceGrid]: {
        formElementId: '',
        formElementTypeId: FormElementTypeId.ValveClearanceGrid,
        formElementLabel: 'Question / Instruction',
        formElementPlaceholder: 'Cylinder',
        selectedOptions: [],
        options: [],
        title: 'Table: Valve Clearance Grid',
        description: 'Valve Clearance Grid',
        iconName: 'short-text',
        required: false,
        formElementTooltip: '',
        formElementFile: {},
        formElementDescription: '',
        formElementHasRule: false,
        grid: [
            {
                rowLabel: 'Intake',
                rowOrder: 1,
                columns: [
                    {
                        columnLabel: '1',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '1',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '2',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                    {
                        columnLabel: '2',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '1',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '2',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                    {
                        columnLabel: '3',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '1',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '2',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                    {
                        columnLabel: '4',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '1',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '2',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                    {
                        columnLabel: '5',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '1',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '2',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                    {
                        columnLabel: '6',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '1',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '2',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                ],
            },
            {
                rowLabel: 'Exhaust',
                rowOrder: 2,
                columns: [
                    {
                        columnLabel: '1',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '1',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '2',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                    {
                        columnLabel: '2',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '1',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '2',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                    {
                        columnLabel: '3',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '1',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '2',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                    {
                        columnLabel: '4',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '1',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '2',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                    {
                        columnLabel: '5',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '1',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '2',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                    {
                        columnLabel: '6',
                        subColumns: [
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '1',
                                columnOrder: 1,
                                defaultValue: '',
                                isRequired: false,
                            },
                            {
                                worksheetElementGridId: '',
                                subColumnLabel: '2',
                                columnOrder: 2,
                                defaultValue: '',
                                isRequired: false,
                            },
                        ],
                    },
                ],
            },
        ],
    },
};
